import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating, PartnerButtons, ProductCard, ComplianceNote, Badge } from "@/components/kit";
import { Gift, Wine, Star } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [score, setScore] = useState(5);
  const [text, setText] = useState("");

  const load = () => api.get(`/products/${id}`).then((x) => setP(x.data));
  useEffect(() => { window.scrollTo(0, 0); load(); }, [id]);
  if (!p) return <div className="p-12 text-center text-muted-foreground">Loading bottle…</div>;

  const submitReview = async () => {
    if (!user) return toast.error("Sign in to review");
    await api.post(`/products/${id}/review`, { score, text }); setText(""); toast.success("Review posted"); load();
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="grid gap-8 lg:grid-cols-2">
        <div className="overflow-hidden rounded-2xl border border-border bg-muted">
          <img src={p.image} alt={p.name} className="aspect-square w-full object-cover" />
        </div>
        <div>
          <span className="overline text-primary">{p.category} · {p.brand}</span>
          <h1 className="mt-2 font-display text-4xl lg:text-5xl">{p.name}</h1>
          <div className="mt-3 flex items-center gap-4"><StarRating value={p.rating} size={18} /><span className="text-sm text-muted-foreground">{p.reviews.toLocaleString()} reviews</span><span className="font-semibold text-muted-foreground">{p.price_band}</span></div>
          <p className="mt-4 text-muted-foreground">{p.tasting}</p>
          <div className="mt-4 flex flex-wrap gap-2">{p.tags?.map((t) => <Badge key={t}>{t}</Badge>)}</div>
          <div className="mt-5 rounded-xl border border-border bg-card p-4"><p className="text-sm font-semibold">Cocktail use & pairing</p><p className="mt-1 text-sm text-muted-foreground">{p.pairing}</p></div>
          <div className="mt-5"><PartnerButtons partners={p.partners} /></div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button data-testid="add-to-registry-btn" onClick={() => toast.success("Added to registry")} className="inline-flex items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-semibold hover:border-primary"><Gift size={15} /> Add to Registry</button>
            <button data-testid="add-to-bar-btn" onClick={() => toast.success("Added to My Bar")} className="inline-flex items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-semibold hover:border-primary"><Wine size={15} /> Add to My Bar</button>
          </div>
        </div>
      </div>

      {/* aggregate + reviews */}
      <div className="mt-12 grid gap-8 lg:grid-cols-[280px_1fr]">
        <div className="rounded-xl border border-border bg-card p-6 text-center lg:sticky lg:top-20 lg:self-start">
          <p className="font-display text-6xl text-primary">{p.rating}</p>
          <StarRating value={p.rating} showNum={false} size={18} />
          <p className="mt-1 text-sm text-muted-foreground">{p.reviews.toLocaleString()} reviews</p>
          <div className="mt-6 border-t border-border pt-4 text-left">
            <p className="mb-2 text-sm font-semibold">Rate this bottle</p>
            <div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => <button key={i} data-testid={`rate-star-${i}`} onClick={() => setScore(i)}><Star size={22} className={i <= score ? "fill-primary text-primary" : "text-muted-foreground"} /></button>)}</div>
            <textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Share your tasting notes…" className="mt-3 w-full resize-none rounded-lg border border-border bg-background/60 p-2 text-sm outline-none focus:border-primary" />
            <button data-testid="submit-review-btn" onClick={submitReview} className="mt-2 w-full rounded-full bg-primary py-2 text-sm font-semibold text-primary-foreground">Post review</button>
          </div>
        </div>
        <div>
          <h2 className="mb-4 font-display text-2xl">Reviews</h2>
          <div className="space-y-4">
            {p.community_reviews?.length ? p.community_reviews.map((rv, k) => (
              <div key={k} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2"><img src={rv.avatar} alt="" className="h-8 w-8 rounded-full object-cover" /><span className="text-sm font-semibold">{rv.author}</span><StarRating value={rv.score} size={12} /></div>
                <p className="mt-2 text-sm text-muted-foreground">{rv.text}</p>
              </div>
            )) : <p className="text-sm text-muted-foreground">Be the first to review this bottle.</p>}
          </div>
          {p.similar?.length > 0 && (
            <div className="mt-10">
              <h2 className="mb-4 font-display text-2xl">Similar bottles</h2>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">{p.similar.map((s) => <ProductCard key={s.id} p={s} />)}</div>
            </div>
          )}
          <div className="mt-8"><ComplianceNote /></div>
        </div>
      </div>
    </div>
  );
}
